import {
  ChevronDown,
  ChevronRight,
  Download,
  Filter,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { CopyButton } from "@/client/components/copy-button";
import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { type AuditSearchInput, auditAdminApi } from "@/client/features/admin/audit-admin-api";
import { type AdminTenant, tenantsAdminApi } from "@/client/features/admin/tenants-admin-api";
import { ApiError } from "@/client/lib/api";
import { formatDateTime } from "@/client/lib/format";
import type { AuditLogRow, AuditMetadataFilter } from "@/shared/audit";

const selectClass =
  "border-input bg-background h-9 rounded-md border px-3 text-sm shadow-xs outline-none focus:border-ring focus:ring-ring/50 focus:ring-[3px]";
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function defaultWindow(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function toLocalInput(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function readMetadata(params: URLSearchParams): AuditMetadataFilter[] {
  const raw = params.get("metadata");
  if (!raw) return [];
  try {
    const value = JSON.parse(raw) as unknown;
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is AuditMetadataFilter =>
        Boolean(item) &&
        typeof item === "object" &&
        "key" in item &&
        typeof item.key === "string" &&
        "value" in item &&
        typeof item.value === "string" &&
        "mode" in item &&
        (item.mode === "exact" || item.mode === "contains"),
    );
  } catch {
    return [];
  }
}

function searchFromParams(params: URLSearchParams): AuditSearchInput {
  const value = (key: string) => params.get(key) || undefined;
  return {
    q: value("q"),
    id: value("id"),
    tenantScope: (value("tenantScope") as AuditSearchInput["tenantScope"]) ?? "all",
    tenantId: value("tenantId"),
    actor: value("actor"),
    actorKind: value("actorKind") as AuditSearchInput["actorKind"],
    action: value("action"),
    targetType: value("targetType"),
    targetId: value("targetId"),
    ip: value("ip"),
    securityReview: params.get("securityReview") === "true",
    from: value("from"),
    to: value("to"),
    metadata: readMetadata(params),
    order: (value("order") as AuditSearchInput["order"]) ?? "newest",
    cursor: value("cursor"),
    limit: 50,
  };
}

function metadataPreview(metadata: Record<string, unknown> | null): string {
  if (!metadata) return "None";
  const text = JSON.stringify(metadata);
  return text.length > 90 ? `${text.slice(0, 87)}...` : text;
}

function ValueWithCopy({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">None</span>;
  return (
    <span className="flex min-w-0 items-start gap-1">
      <span className="min-w-0 font-mono text-xs break-all">{value}</span>
      <CopyButton value={value} />
    </span>
  );
}

function EntryDetails({ entry }: { entry: AuditLogRow }) {
  const fields: Array<[string, string | null]> = [
    ["Audit ID", entry.id],
    ["Tenant ID", entry.tenantId],
    ["Actor user ID", entry.actorUserId],
    ["Actor kind", entry.actorKind],
    ["Target type", entry.targetType],
    ["Target ID", entry.targetId],
    ["IP address", entry.ip],
    ["Created UTC", entry.createdAt],
  ];
  return (
    <div className="bg-muted/30 space-y-4 p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {fields.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <p className="text-muted-foreground mb-1 text-xs font-medium">{label}</p>
            <ValueWithCopy value={value} />
          </div>
        ))}
      </div>
      <div>
        <p className="text-muted-foreground mb-1 text-xs font-medium">Metadata</p>
        <pre className="bg-background max-h-72 overflow-auto rounded-md border p-3 font-mono text-xs">
          {entry.metadata ? JSON.stringify(entry.metadata, null, 2) : "null"}
        </pre>
      </div>
    </div>
  );
}

export function StaffAuditLogPage() {
  const [params, setParams] = useSearchParams();
  const [entries, setEntries] = useState<AuditLogRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [advanced, setAdvanced] = useState(false);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([]);
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [exporting, setExporting] = useState(false);
  const [auditId, setAuditId] = useState(params.get("id") ?? "");
  const [actor, setActor] = useState(params.get("actor") ?? "");
  const urlQuery = params.get("q") ?? "";
  const urlAuditId = params.get("id") ?? "";
  const urlActor = params.get("actor") ?? "";
  const securityReview = params.get("securityReview") === "true";

  useEffect(() => {
    if (!params.get("from") && !params.get("to")) {
      const window = defaultWindow();
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("from", window.from);
          next.set("to", window.to);
          return next;
        },
        { replace: true },
      );
    }
  }, [params, setParams]);

  useEffect(() => setQuery(urlQuery), [urlQuery]);
  useEffect(() => setAuditId(urlAuditId), [urlAuditId]);
  useEffect(() => setActor(urlActor), [urlActor]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = query.trim();
      if (normalized === urlQuery) return;
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        if (normalized) next.set("q", normalized);
        else next.delete("q");
        next.delete("cursor");
        return next;
      });
      setCursorHistory([]);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query, setParams, urlQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = actor.trim();
      if (normalized === urlActor) return;
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        if (normalized) next.set("actor", normalized);
        else next.delete("actor");
        next.delete("cursor");
        return next;
      });
      setCursorHistory([]);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [actor, setParams, urlActor]);

  useEffect(() => {
    tenantsAdminApi
      .list()
      .then((response) => setTenants(response.tenants))
      .catch(() => undefined);
  }, []);

  const search = useMemo(() => searchFromParams(params), [params]);
  const searchKey = params.toString();

  useEffect(() => {
    const controller = new AbortController();
    setEntries(null);
    setError(false);
    auditAdminApi
      .search(search, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setEntries(response.entries);
        setTotal(response.total);
        setHasMore(response.hasMore);
        setNextCursor(response.nextCursor);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load the audit log.");
      });
    return () => controller.abort();
  }, [search, searchKey]);

  const setFilters = (updates: Record<string, string | undefined>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      next.delete("cursor");
      return next;
    });
    setCursorHistory([]);
  };
  const setFilter = (key: string, value: string | undefined) => setFilters({ [key]: value });
  const applyUuidFilter = (key: "id", value: string) => {
    const normalized = value.trim();
    if (!normalized || uuidPattern.test(normalized)) {
      setFilter(key, normalized || undefined);
    }
  };

  const metadata = readMetadata(params);
  const setMetadata = (filters: AuditMetadataFilter[]) => {
    setFilter("metadata", filters.length > 0 ? JSON.stringify(filters) : undefined);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await auditAdminApi.exportCsv(search);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to export the audit log.");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    const window = defaultWindow();
    setQuery("");
    setAuditId("");
    setActor("");
    setParams({ from: window.from, to: window.to });
    setCursorHistory([]);
    setAdvanced(false);
  };

  const nextPage = () => {
    if (!nextCursor) return;
    setCursorHistory((history) => [...history, params.get("cursor")]);
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set("cursor", nextCursor);
      return next;
    });
  };

  const previousPage = () => {
    const previous = cursorHistory[cursorHistory.length - 1];
    setCursorHistory((history) => history.slice(0, -1));
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (previous) next.set("cursor", previous);
      else next.delete("cursor");
      return next;
    });
  };

  const actionOptions = Array.from(new Set(entries?.map((entry) => entry.action) ?? [])).sort();
  const hasActiveFilters = Array.from(params.keys()).some(
    (key) => !["from", "to", "cursor"].includes(key),
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Audit Log"
        description="Search sensitive activity across tenants and export a filtered subset."
      >
        <Button variant="outline" disabled={entries === null || exporting} onClick={exportCsv}>
          <Download />
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader className="gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Search</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={securityReview ? "default" : "outline"}
                aria-pressed={securityReview}
                onClick={() => setFilter("securityReview", securityReview ? undefined : "true")}
                title="Show only credential, access, privilege, and destructive events worth a security review"
              >
                <ShieldAlert />
                Suspicious only
              </Button>
              <Button
                type="button"
                size="sm"
                variant={advanced ? "secondary" : "outline"}
                onClick={() => setAdvanced((value) => !value)}
              >
                <Filter />
                Advanced
              </Button>
              {hasActiveFilters ? (
                <Button type="button" size="sm" variant="ghost" onClick={resetFilters}>
                  Reset
                </Button>
              ) : null}
            </div>
          </div>
          <div className="relative">
            <Search className="text-muted-foreground absolute top-2.5 left-3 size-4" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="pl-9"
              placeholder="Search IDs, actions, people, tenants, targets, IPs, or metadata"
              aria-label="Search all audit fields"
            />
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium">From</span>
              <Input
                type="datetime-local"
                value={toLocalInput(search.from)}
                onChange={(event) => setFilter("from", fromLocalInput(event.target.value))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium">To</span>
              <Input
                type="datetime-local"
                value={toLocalInput(search.to)}
                onChange={(event) => setFilter("to", fromLocalInput(event.target.value))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium">Tenant</span>
              <select
                className={`${selectClass} w-full`}
                value={search.tenantId ?? ""}
                onChange={(event) => {
                  setFilters({
                    tenantId: event.target.value || undefined,
                    tenantScope: event.target.value ? "tenant" : undefined,
                  });
                }}
              >
                <option value="">All tenants</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium">Action</span>
              <Input
                list="audit-actions"
                value={search.action ?? ""}
                onChange={(event) => setFilter("action", event.target.value || undefined)}
                placeholder="Exact action"
              />
              <datalist id="audit-actions">
                {actionOptions.map((action) => (
                  <option key={action} value={action} />
                ))}
              </datalist>
            </label>
          </div>

          {advanced ? (
            <div className="border-border space-y-4 border-t pt-4">
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Audit ID</span>
                  <Input
                    value={auditId}
                    onChange={(event) => setAuditId(event.target.value)}
                    onBlur={() => applyUuidFilter("id", auditId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                    }}
                    aria-invalid={Boolean(auditId && !uuidPattern.test(auditId.trim()))}
                    placeholder="UUID"
                  />
                  {auditId && !uuidPattern.test(auditId.trim()) ? (
                    <span className="text-destructive text-xs">
                      Enter a complete UUID to apply.
                    </span>
                  ) : null}
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Tenant scope</span>
                  <select
                    className={`${selectClass} w-full`}
                    value={search.tenantScope ?? "all"}
                    onChange={(event) => {
                      const value = event.target.value;
                      setFilters({
                        tenantScope: value === "all" ? undefined : value,
                        tenantId: value === "global" ? undefined : search.tenantId,
                      });
                    }}
                  >
                    <option value="all">All</option>
                    <option value="tenant">Tenant events</option>
                    <option value="global">Global events</option>
                  </select>
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Actor</span>
                  <Input
                    value={actor}
                    onChange={(event) => setActor(event.target.value)}
                    placeholder="Name, email, or user ID"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Actor kind</span>
                  <select
                    className={`${selectClass} w-full`}
                    value={search.actorKind ?? ""}
                    onChange={(event) => setFilter("actorKind", event.target.value || undefined)}
                  >
                    <option value="">Any</option>
                    <option value="customer">Customer</option>
                    <option value="staff">Staff</option>
                  </select>
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Target type</span>
                  <Input
                    value={search.targetType ?? ""}
                    onChange={(event) => setFilter("targetType", event.target.value || undefined)}
                    placeholder="Exact target type"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Target ID</span>
                  <Input
                    value={search.targetId ?? ""}
                    onChange={(event) => setFilter("targetId", event.target.value || undefined)}
                    placeholder="Exact target ID"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">IP address</span>
                  <Input
                    value={search.ip ?? ""}
                    onChange={(event) => setFilter("ip", event.target.value || undefined)}
                    placeholder="Exact IP"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground text-xs font-medium">Order</span>
                  <select
                    className={`${selectClass} w-full`}
                    value={search.order ?? "newest"}
                    onChange={(event) => setFilter("order", event.target.value)}
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                </label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Metadata filters</p>
                    <p className="text-muted-foreground text-xs">
                      Match up to five top-level JSON keys.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={metadata.length >= 5}
                    onClick={() =>
                      setMetadata([...metadata, { key: "", value: "", mode: "exact" }])
                    }
                  >
                    <Plus />
                    Add filter
                  </Button>
                </div>
                {metadata.map((filter, index) => (
                  <div key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_9rem_auto]">
                    <Input
                      value={filter.key}
                      onChange={(event) => {
                        const next = [...metadata];
                        next[index] = { ...filter, key: event.target.value };
                        setMetadata(next);
                      }}
                      placeholder="Key"
                      aria-label={`Metadata key ${index + 1}`}
                    />
                    <Input
                      value={filter.value}
                      onChange={(event) => {
                        const next = [...metadata];
                        next[index] = { ...filter, value: event.target.value };
                        setMetadata(next);
                      }}
                      placeholder="Value"
                      aria-label={`Metadata value ${index + 1}`}
                    />
                    <select
                      className={selectClass}
                      value={filter.mode}
                      onChange={(event) => {
                        const next = [...metadata];
                        next[index] = {
                          ...filter,
                          mode: event.target.value as AuditMetadataFilter["mode"],
                        };
                        setMetadata(next);
                      }}
                      aria-label={`Metadata match mode ${index + 1}`}
                    >
                      <option value="exact">Exact</option>
                      <option value="contains">Contains</option>
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setMetadata(metadata.filter((_, item) => item !== index))}
                      aria-label={`Remove metadata filter ${index + 1}`}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Events</CardTitle>
          {entries !== null ? (
            <span className="text-muted-foreground text-sm">
              {total.toLocaleString()} {total === 1 ? "result" : "results"}
            </span>
          ) : null}
        </CardHeader>
        <CardContent>
          {error && entries === null ? (
            <p className="text-muted-foreground text-sm">
              Couldn&apos;t load the audit log. Check the filters and try again.
            </p>
          ) : entries === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : entries.length === 0 ? (
            <p className="text-muted-foreground text-sm">No events match these filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="w-8 py-2 pr-2 font-medium">
                      <span className="sr-only">Details</span>
                    </th>
                    <th className="py-2 pr-4 font-medium">Time</th>
                    <th className="py-2 pr-4 font-medium">Action</th>
                    <th className="py-2 pr-4 font-medium">Actor</th>
                    <th className="py-2 pr-4 font-medium">Tenant</th>
                    <th className="py-2 pr-4 font-medium">Target</th>
                    <th className="py-2 pr-4 font-medium">IP</th>
                    <th className="py-2 font-medium">Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const isExpanded = expanded.has(entry.id);
                    return (
                      <Fragment key={entry.id}>
                        <tr className="border-b">
                          <td className="py-2 pr-2 align-top">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-7"
                              onClick={() =>
                                setExpanded((current) => {
                                  const next = new Set(current);
                                  if (isExpanded) next.delete(entry.id);
                                  else next.add(entry.id);
                                  return next;
                                })
                              }
                              aria-label={isExpanded ? "Collapse event" : "Expand event"}
                            >
                              {isExpanded ? <ChevronDown /> : <ChevronRight />}
                            </Button>
                          </td>
                          <td className="whitespace-nowrap py-2 pr-4 align-top">
                            {formatDateTime(entry.createdAt)}
                          </td>
                          <td className="py-2 pr-4 align-top">
                            <Badge variant="outline" className="font-mono">
                              {entry.action}
                            </Badge>
                          </td>
                          <td className="max-w-48 py-2 pr-4 align-top">
                            <p className="truncate font-medium">
                              {entry.actorName || entry.actorEmail || "System"}
                            </p>
                            {entry.actorEmail && entry.actorName ? (
                              <p className="text-muted-foreground truncate text-xs">
                                {entry.actorEmail}
                              </p>
                            ) : null}
                          </td>
                          <td className="max-w-40 py-2 pr-4 align-top">
                            <p className="truncate">
                              {entry.tenantName ?? (entry.tenantId ? "Deleted tenant" : "Global")}
                            </p>
                          </td>
                          <td className="max-w-48 py-2 pr-4 align-top">
                            {entry.targetType ? (
                              <>
                                <p>{entry.targetType}</p>
                                <p className="text-muted-foreground truncate font-mono text-xs">
                                  {entry.targetId ?? "None"}
                                </p>
                              </>
                            ) : (
                              <span className="text-muted-foreground">None</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap py-2 pr-4 align-top font-mono text-xs">
                            {entry.ip ?? "None"}
                          </td>
                          <td className="max-w-64 py-2 align-top">
                            <span className="text-muted-foreground block truncate font-mono text-xs">
                              {metadataPreview(entry.metadata)}
                            </span>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr className="border-b">
                            <td colSpan={8}>
                              <EntryDetails entry={entry} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {entries !== null && entries.length > 0 ? (
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-muted-foreground text-xs">
                Page {cursorHistory.length + 1}, showing {entries.length} events
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={cursorHistory.length === 0}
                  onClick={previousPage}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!hasMore || !nextCursor}
                  onClick={nextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
